import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from "react-redux";
import { addDays, compareAsc } from "date-fns";
import { format, formatInTimeZone } from "date-fns-tz";
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Visibility, Warning } from "@mui/icons-material";
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { filterCrewRoutes } from 'state/helpers';
import { logClientAction } from 'state/logger';
import { hasPermission } from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import ExportButton from 'components/ExportButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import CheckResultDialog from 'components/CheckResultDialog';
import RouteFilterDialog from 'components/RouteFilterDialog';
import RouteCrewListDialog from 'components/RouteCrewListDialog';
import PairingList from "./PairingList";


/* ── Module-level constants ────────────────────────────────────────── */
const DELAY_THRESHOLDS = {
  HIGH:   { minutes: 60, color: '#f2292d' },
  MEDIUM: { minutes: 30, color: '#f29129' },
  LOW:    { minutes: 15, color: '#d7f229' },
};

const buildOptions = (data, key) =>
  [...new Set(data.map(item => item[key]))].sort();

const fmtCrewComplement = (items) => {
  if (!Array.isArray(items) || !items.length) return '';
  return items
    .map(({ rankCode, quantity, isCompulsory, actual, isMet }) =>
      `${rankCode}×${quantity}${isCompulsory ? '*' : ''} ${isMet ? '✓' : `✗ ${actual ?? 0}/${quantity}`}`
    )
    .join('\n');
};


/* ── Component ─────────────────────────────────────────────────────── */
const CrewPairings = () => {
  useAuthCheck();
  const user     = useSelector((state) => state.user);
  const timeMode = useSelector((state) => state.timemode);
  const params   = useSelector((state) => state.params);
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canView  = hasPermission(user?.permissions, "CrewPairings", "view",  false);
  const canCheck = hasPermission(user?.permissions, "CrewPairings", "check", false);

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const [checksList,            setChecksList]            = useState([]);
  const [showCheckResultDialog, setShowCheckResultDialog] = useState(false);
  const [isFilterDialogOpen,    setIsFilterDialogOpen]    = useState(false);
  const [filters,               setFilters]               = useState(null);
  const [openRouteCrewListDialog, setOpenRouteCrewListDialog] = useState(false);
  const [crewList,   setCrewList]   = useState([]);
  const [crewRoute,  setCrewRoute]  = useState({});
  const [crewPairings, setCrewPairings] = useState([]);

  const minCrewRouteTAT = Number(params?.find((p) => p.param === "MINIMUM_CREW_ROUTE_TAT_MINUTES")?.value) || 80;

  /* ── Data fetching ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!startDate || !endDate) return;
    const fetchData = async () => {
      setError(null);
      setLoading(true);
      try {
        const res = await api.post('/flights/crewpairings', {
          startDate: addDays(startDate, -1),
          endDate:   addDays(endDate, 1),
        });
        if (res.data) {
          setCrewPairings(res.data.filter(r => {
            const d = timeMode === 'UTC' ? r.routeDate : r.routeDateLocalBase;
            return compareAsc(d, startDate) >= 0 && compareAsc(d, endDate) <= 0;
          }));
        }
      } catch (err) {
        setError(err);
        toast.error(err?.message || "Failed to fetch crew pairings!");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [timeMode, startDate, endDate]);

  const filteredCrewRoutes = useMemo(
    () => filterCrewRoutes({ crewRoutes: crewPairings, filters, timeMode }),
    [crewPairings, filters, timeMode]
  );

  /* ── Handlers ──────────────────────────────────────────────────── */
  const handleCheckErrors = useCallback(() => {
    try {
      const checks = [];
      const toCheck = crewPairings.filter(r =>
        r.routeDetails?.some(f => f.diffRegScheduleTat !== null && f.diffRegScheduleTat < minCrewRouteTAT)
      );

      toCheck.forEach(({ routeDate, routeDateLocalBase, routeNo, flightNo, crewRoute, routeDetails }) => {
        const formattedDate = format(
          new Date(timeMode === "UTC" ? routeDate : routeDateLocalBase),
          "dd/MM/yy"
        );
        if (!flightNo || String(flightNo).trim() === '') {
          checks.push({ routeDate: formattedDate, routeNo, error: 'Missing flight number' });
        }
        routeDetails.forEach(f => {
          if (f.diffRegScheduleTat !== null && f.diffRegScheduleTat < minCrewRouteTAT) {
            checks.push({ routeDate: formattedDate, routeNo, flightNo, crewRoute,
              error: `Flight No. ${f.flightNo} has TAT ${f.diffRegScheduleTat} mins`,
            });
          }
        });
      });

      if (checks.length > 0) {
        setChecksList(checks);
        setShowCheckResultDialog(true);
      } else {
        toast.success("No errors found.");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to check errors!");
    }
  }, [timeMode, crewPairings, minCrewRouteTAT]);

  const handleViewClick = useCallback((param) => {
    const fetchLegMembers = async () => {
      try {
        const res = await api.post('/legmembers/legmembersbyrouteno', {
          routeDate: new Date(param.routeDate),
          routeNo:   param.routeNo,
        });
        if (!res.data) { toast.error("No data found for this route!"); return; }

        const uniqueCrew = Object.values(
          res.data.reduce((acc, item) => {
            if (!item.crewCode || acc[item.crewCode]) return acc;
            acc[item.crewCode] = {
              crewCode: item.crewCode, crewName: item.crewName,
              post: item.post, base: item.base, gender: item.gender,
              contactNo: item.contactNo, rankPriority: item.rankPriority,
            };
            return acc;
          }, {})
        ).sort((a, b) => a.rankPriority - b.rankPriority);

        const routeMap = res.data.reduce((acc, item) => {
          if (!item.routeNo) return acc;
          const key = `${item.routeDate}|${item.routeNo}`;
          if (!acc[key]) acc[key] = { routeDate: item.routeDate, routeNo: item.routeNo, routeDetails: [], _keys: new Set() };
          const fKey = `${item.flightDate}|${item.flightDesc}|${item.dep}`;
          if (!acc[key]._keys.has(fKey)) {
            acc[key].routeDetails.push({
              flightDate: item.flightDate, flightDesc: item.flightDesc,
              dep: item.dep, arr: item.arr, flightNo: item.flightNo,
              acType: item.acType, flightReg: item.flightReg,
              etd: item.etd, eta: item.eta, etdLocalBase: item.etdLocalBase, etaLocalBase: item.etaLocalBase,
              atd: item.atd, ata: item.ata, atdLocalBase: item.atdLocalBase, ataLocalBase: item.ataLocalBase,
              fullEtd: item.fullEtd, fullEta: item.fullEta,
              fullEtdLocalBase: item.fullEtdLocalBase, fullEtaLocalBase: item.fullEtaLocalBase,
              fullAtd: item.fullAtd, fullAta: item.fullAta,
              fullAtdLocalBase: item.fullAtdLocalBase, fullAtaLocalBase: item.fullAtaLocalBase,
              blockTime: item.blockTime,
            });
            acc[key]._keys.add(fKey);
          }
          return acc;
        }, {});

        const routes = Object.values(routeMap).map(r => {
          delete r._keys;
          r.routeDetails.sort((a, b) => new Date(a.fullEtd) - new Date(b.fullEtd));
          const first = r.routeDetails[0];
          const last  = r.routeDetails[r.routeDetails.length - 1];
          r.fullEtd          = first?.fullEtd          || null;
          r.fullEtdLocalBase = first?.fullEtdLocalBase || null;
          r.fullAtd          = first?.fullAtd          || null;
          r.fullAtdLocalBase = first?.fullAtdLocalBase || null;
          r.fullEta          = last?.fullEta           || null;
          r.fullEtaLocalBase = last?.fullEtaLocalBase  || null;
          r.fullAta          = last?.fullAta           || null;
          r.fullAtaLocalBase = last?.fullAtaLocalBase  || null;
          return r;
        });

        setCrewRoute(routes[0] || param);
        setCrewList(uniqueCrew);
        setOpenRouteCrewListDialog(true);
      } catch (err) {
        toast.error(err.message || "Failed to fetch leg members!");
      }
    };
    fetchLegMembers();
  }, []);

  /* ── Derived ───────────────────────────────────────────────────── */
  const { acTypeOptions, flightRegOptions, depOptions, arrOptions } = useMemo(() => ({
    acTypeOptions:    buildOptions(crewPairings, 'acType'),
    flightRegOptions: buildOptions(crewPairings, 'flightReg'),
    depOptions:       buildOptions(crewPairings, 'dep'),
    arrOptions:       buildOptions(crewPairings, 'arr'),
  }), [crewPairings]);

  const exportRows = useMemo(() => filteredCrewRoutes.map((route) => {
    const {
      routeDetails = [], crewComplementRequired,
      routeDateLocalBase,
      fullRpt, fullEtd, fullEta, fullAtd, fullAta, fullEod, fullEEod, fullAEod,
      fullRptLocalBase, fullEtdLocalBase, fullEtaLocalBase, fullAtdLocalBase, fullAtaLocalBase,
      fullEodLocalBase, fullEEodLocalBase, fullAEodLocalBase,
      rptLocalBase, etdLocalBase, etaLocalBase, atdLocalBase, ataLocalBase, eodLocalBase,
      ...rest
    } = route;
    return {
      ...rest,
      ...(crewComplementRequired?.length && { crewComplementRequired: fmtCrewComplement(crewComplementRequired) }),
      routeDetails: routeDetails.map(f => {
        const dateStr = f.flightDate ? format(new Date(f.flightDate), "dd/MM/yyyy") : "";
        return `${f.flightNo || ""} ${f.flightReg || ""} ${dateStr} ${f.etd || ""} ${f.dep || ""} ${f.arr || ""} ${f.eta || ""}`;
      }).join("\n"),
    };
  }), [filteredCrewRoutes]);

  const getAtd = useCallback((_, row) =>
    timeMode === "UTC" ? row.atd || row.etd || "" : row.atdLocalBase || row.etdLocalBase || "",
  [timeMode]);

  const getAta = useCallback((_, row) =>
    timeMode === "UTC" ? row.ata || row.eta || "" : row.ataLocalBase || row.etaLocalBase || "",
  [timeMode]);

  /* ── Columns ───────────────────────────────────────────────────── */
  const columns = useMemo(() => [
    {
      field: "no", headerName: "No",
      ...isMobile ? { flex: 1 } : { width: 50 },
      filterable: false, sortable: false,
      renderCell: (p) => p.api.getAllRowIds().indexOf(p.id) + 1,
    },
    {
      field: 'routeDetails', headerName: 'Legs',
      ...isMobile ? { flex: 5 } : { width: 350 },
      renderCell: (p) => {
        const hasWarning = p.row?.routeDetails?.some(f => f.diffRegScheduleTat !== null && f.diffRegScheduleTat < minCrewRouteTAT);
        return (
          <Box sx={{ height: '100%', alignItems: 'flex-start', overflowY: 'auto', overflowX: 'hidden',
            animation: hasWarning ? 'glow 1.5s ease-in-out infinite' : 'none',
            '@keyframes glow': { '0%': { boxShadow: '0 0 0px orange' }, '50%': { boxShadow: '0 0 10px orange' }, '100%': { boxShadow: '0 0 0px orange' } },
          }}>
            {p.row?.routeDetails.map((f, i) => {
              const fullAtd = timeMode === 'UTC'
                ? formatInTimeZone(f.fullAtd || f.fullEtd, "UTC", "ddMMMyy")
                : formatInTimeZone(f.fullAtdLocalBase || f.fullEtdLocalBase, "UTC", "ddMMMyy");
              const atd = timeMode === 'UTC' ? (f?.atd || f?.etd) : (f?.atdLocalBase || f?.etdLocalBase);
              const ata = timeMode === 'UTC' ? (f?.ata || f?.eta) : (f?.ataLocalBase || f?.etaLocalBase);
              return (
                <Box key={i} display="flex" alignItems="center" gap={1}
                  sx={{ color: f.diffRegScheduleTat && f.diffRegScheduleTat < minCrewRouteTAT ? theme.palette.warning.main : 'inherit' }}
                >
                  <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 30 }}>{f.flightNo}</Typography>
                  {!isMobile && <Typography variant="body2" sx={{ textAlign: 'center', width: 54 }}>{f.flightReg || f.acType}</Typography>}
                  <Typography variant="body2" sx={{ width: 45 }}>{fullAtd}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 30 }}>{atd}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 25 }}>{f.dep}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 25 }}>{f.arr}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'center', width: 30 }}>{ata}</Typography>
                  <Typography variant="body2" sx={{ textAlign: 'left', width: 8 }}>{f.pax}</Typography>
                </Box>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: timeMode === 'UTC' ? "routeDate" : "routeDateLocalBase", headerName: "RouteDate", width: 100,
      valueFormatter: (p) => format(new Date(p), "dd/MM/yy"),
    },
    { field: "routeNo",   headerName: "RouteNo",   width: 88 },
    { field: "flightNo",  headerName: "FlightNo",  width: 220 },
    { field: "crewRoute", headerName: "CrewRoute", width: 260, cellClassName: "name-column--cell" },
    { field: "acType",    headerName: "ACType",    width: 88 },
    { field: "flightReg", headerName: "FlightReg", width: 100 },
    { field: "dep",       headerName: "Dep",       width: 88 },
    { field: "arr",       headerName: "Arr",       width: 88 },
    { field: timeMode === 'UTC' ? "rpt" : "rptLocalBase", headerName: "Rpt", width: 88 },
    { field: timeMode === 'UTC' ? "std" : "stdLocalBase", headerName: "Std", width: 88 },
    { field: timeMode === 'UTC' ? "sta" : "staLocalBase", headerName: "Sta", width: 88 },
    { field: timeMode === 'UTC' ? "etd" : "etdLocalBase", headerName: "Etd", width: 88 },
    { field: timeMode === 'UTC' ? "eta" : "etaLocalBase", headerName: "Eta", width: 88 },
    { field: "atdDisplay", headerName: "Atd", width: 88, valueGetter: getAtd },
    { field: "ataDisplay", headerName: "Ata", width: 88, valueGetter: getAta },
    {
      field: "eod", headerName: "Eod", width: 88,
      renderCell: ({ row }) => {
        const aeod = timeMode === "UTC" ? row.fullAEod || row.fullEEod : row.fullAEodLocalBase || row.fullEEodLocalBase;
        const seod = timeMode === "UTC" ? row.fullSEod : row.fullSEodLocalBase;
        let cellStyle = {};
        if (aeod && seod) {
          const diffMinutes = (new Date(aeod) - new Date(seod)) / 60000;
          if (diffMinutes > 15) {
            const aeodDate = new Date(aeod);
            const today    = new Date();
            const isSameDay = timeMode === "UTC"
              ? aeodDate.getUTCFullYear() === today.getUTCFullYear() && aeodDate.getUTCMonth() === today.getUTCMonth() && aeodDate.getUTCDate() === today.getUTCDate()
              : aeodDate.getFullYear() === today.getFullYear() && aeodDate.getMonth() === today.getMonth() && aeodDate.getDate() === today.getDate();
            const color = diffMinutes > DELAY_THRESHOLDS.HIGH.minutes   ? DELAY_THRESHOLDS.HIGH.color
                        : diffMinutes > DELAY_THRESHOLDS.MEDIUM.minutes ? DELAY_THRESHOLDS.MEDIUM.color
                        : DELAY_THRESHOLDS.LOW.color;
            cellStyle = isSameDay
              ? { color, animation: "blink 1s step-start 0s infinite", fontWeight: "bold", "@keyframes blink": { "60%": { opacity: 0.3 } } }
              : { color };
          }
        }
        return (
          <Typography height="100%" display="flex" alignItems="center" variant="body2" sx={cellStyle}>
            {aeod && formatInTimeZone(aeod, "UTC", "HH:mm")}
          </Typography>
        );
      },
    },
    { field: "elapsedHours",   headerName: "ElapsedHours",   width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(2) : 0 },
    { field: "routeDays",      headerName: "RouteDays",      width: 100, align: 'right' },
    { field: "blockHours",     headerName: "BH",             width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(2) : 0 },
    { field: "sectors",        headerName: "Sectors",        width: 100, align: 'right' },
    { field: "perdiemMinutes", headerName: "PerdiemMinutes", width: 100, align: 'right', valueFormatter: (p) => p > 0 ? parseFloat(p).toFixed(0) : 0 },
    { field: "cLounge",        headerName: "CLounge",        width: 100, align: 'right' },
    { field: "hotelDays",      headerName: "HotelDays",      width: 100, align: 'right' },
  ], [timeMode, theme, isMobile, minCrewRouteTAT, getAtd, getAta]);

  const actionColumn = useMemo(() => {
    if (!canView) return [];
    return [{
      field: "action", headerName: "Action", filterable: false, sortable: false,
      ...isMobile ? { flex: 1 } : { width: 85 },
      renderCell: (p) => (
        <Box height="100%" display="flex" justifyContent="center" alignItems="center">
          <Tooltip title="View">
            <IconButton
              sx={{ color: theme.palette.primary.dark, '&:hover': { color: theme.palette.green.deep } }}
              onClick={() => handleViewClick(p.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    }];
  }, [canView, theme, isMobile, handleViewClick]);

  const hiddenColumns = useMemo(() => ({
    routeDate: false, routeDateLocalBase: false, routeNo: false, flightNo: false,
    crewRoute: false, acType: false, flightReg: false, dep: false, arr: false,
    rpt: false, rptLocalBase: false, std: false, stdLocalBase: false,
    sta: false, staLocalBase: false, etd: false, etdLocalBase: false,
    eta: false, etaLocalBase: false, atd: false, atdLocalBase: false,
    ata: false, ataLocalBase: false, atdDisplay: false, ataDisplay: false,
    blockHours: false, sectors: false, elapsedHours: false, routeDays: false,
    cLounge: false, hotelDays: false, perdiemMinutes: false,
  }), []);

  const nonMobileHiddenColumns = useMemo(() => ({
    std: false, stdLocalBase: false, sta: false, staLocalBase: false,
    etd: false, etdLocalBase: false, eta: false, etaLocalBase: false,
    atd: false, atdLocalBase: false, ata: false, ataLocalBase: false,
  }), []);

  const columnsToShow = useMemo(
    () => [...columns, ...actionColumn],
    [columns, actionColumn]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Crew Pairings"
          subtitle={loading ? "Loading..." : `List of ${filteredCrewRoutes?.length || 0} Pairings`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {canCheck && (
              <Tooltip title="Check errors" arrow>
                <span>
                  <StyledButton onClick={handleCheckErrors}>
                    <Warning />
                  </StyledButton>
                </span>
              </Tooltip>
            )}

            <ExportButton
              data={exportRows}
              fileName="CrewPairings"
              formats={['xlsx', 'csv', 'pdf']}
              onExport={(fmt) => logClientAction("EXPORT_REPORT", { fileName: "CrewPairings", format: fmt, startDate, endDate })}
            />

            <FilterButton filters={filters} onClick={() => setIsFilterDialogOpen(true)} />
          </Box>
        )}
      </FlexBetween>

      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2}
        >
          <Box 
            ref={scrollRef} 
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <PairingList 
                routes={filteredCrewRoutes} 
                onView={handleViewClick} 
              />
            ) : (
              <DataGridComponent
                rows={filteredCrewRoutes}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
              />
            )}
            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      {isFilterDialogOpen && (
        <RouteFilterDialog
          open={isFilterDialogOpen}
          onClose={() => setIsFilterDialogOpen(false)}
          onApply={setFilters}
          acTypeOptions={acTypeOptions}
          flightRegOptions={flightRegOptions}
          depOptions={depOptions}
          arrOptions={arrOptions}
          currentFilters={filters}
        />
      )}
      {showCheckResultDialog && (
        <CheckResultDialog
          open={showCheckResultDialog}
          onClose={() => setShowCheckResultDialog(false)}
          title="Checks"
          checksList={checksList}
          descriptionKey="error"
        />
      )}
      {openRouteCrewListDialog && (
        <RouteCrewListDialog
          open={openRouteCrewListDialog}
          onClose={() => { setOpenRouteCrewListDialog(false); setCrewRoute({}); setCrewList([]); }}
          crewRoute={crewRoute}
          crewList={crewList}
        />
      )}
    </Box>
  );
};

export default CrewPairings;
